import type { Mutation, OutboxEntry } from './types';

/**
 * The outbox is a plain array kept in the store (and therefore persisted with
 * it). Entries are keyed by mutation id so re-editing the same table before a
 * flush collapses into one write instead of a queue of stale carts.
 */
export function enqueue(outbox: OutboxEntry[], mutation: Mutation): OutboxEntry[] {
  const entry: OutboxEntry = { mutation, queuedAt: Date.now(), attempts: 0 };

  // Orders are append-only and each has its own uuid, so they never collapse.
  if (mutation.kind === 'order') return [...outbox, entry];

  const idx = outbox.findIndex(
    (e) => e.mutation.kind === mutation.kind && e.mutation.id === mutation.id,
  );
  if (idx === -1) return [...outbox, entry];

  const next = outbox.slice();
  // Keep the original queue position so ordering against other writes holds.
  next[idx] = { ...entry, queuedAt: next[idx].queuedAt };
  return next;
}

export function dequeue(outbox: OutboxEntry[], mutation: Mutation): OutboxEntry[] {
  return outbox.filter(
    (e) => !(e.mutation.kind === mutation.kind && e.mutation.id === mutation.id),
  );
}

export function markFailed(
  outbox: OutboxEntry[],
  mutation: Mutation,
  error: string,
): OutboxEntry[] {
  return outbox.map((e) =>
    e.mutation.kind === mutation.kind && e.mutation.id === mutation.id
      ? { ...e, attempts: e.attempts + 1, lastError: error }
      : e,
  );
}

/** Oldest first: a table's clear must not overtake the order that emptied it. */
export function sorted(outbox: OutboxEntry[]): OutboxEntry[] {
  return [...outbox].sort((a, b) => a.queuedAt - b.queuedAt);
}
